import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import requestLogsHelper from "../../API/RequestLogs/requestLogsHelper";
import { isLargeScreen } from "../../Screens/MainViewContainer";
import ToothFairyButton from "../ToothFairyComponents/ToothFairyButton";
import ToothFairyLoadingPlaceholder from "../ToothFairyComponents/ToothFairyLoadingPlaceholder";
import ToothFairyModal from "../ToothFairyComponents/ToothFairyModal";

const modelTypeLabels = {
  extraSmall: "XS",
  small: "S",
  medium: "M",
  large: "L",
  extraLarge: "XL",
  ultra: "UL",
};

const USD_PER_UOI = 0.01;

const formatUoI = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return "0";
  const uoi = num / USD_PER_UOI;
  if (uoi === 0) return "0";
  if (uoi < 1) return uoi.toFixed(2);
  if (uoi < 100) return uoi.toFixed(2);
  return uoi.toFixed(0);
};

const truncateId = (id, len = 12) => {
  if (!id) return "";
  return id.length > len ? `${id.slice(0, len)}...` : id;
};

const UoIUsageModal = ({ visible, setVisible, chatId, messages = [] }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!visible || !chatId) return;
    let isMounted = true;
    const fetchLogs = async () => {
      setIsLoading(true);
      setError(null);
      setLogs([]);
      setExpandedMessages({});
      try {
        const items = await requestLogsHelper.getAllRequestLogsByChat(chatId);
        if (isMounted) {
          setLogs(items);
          const grouped = groupByMessage(items);
          const initialExpanded = {};
          Object.keys(grouped).forEach((msgId, index) => {
            initialExpanded[msgId] = index === 0;
          });
          setExpandedMessages(initialExpanded);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.message || "Failed to load usage data");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchLogs();
    return () => {
      isMounted = false;
    };
  }, [visible, chatId]);

  const groupByMessage = (allLogs) => {
    const groups = {};
    allLogs.forEach((log) => {
      const msgId = log?.messageID;
      if (!msgId) return;
      if (!groups[msgId]) groups[msgId] = [];
      groups[msgId].push(log);
    });
    Object.keys(groups).forEach((msgId) => {
      groups[msgId].sort(
        (a, b) => (a?.creationTime || 0) - (b?.creationTime || 0),
      );
    });
    return groups;
  };

  const groupedLogs = useMemo(() => groupByMessage(logs), [logs]);

  const messageOrder = useMemo(() => {
    return Object.keys(groupedLogs).sort((a, b) => {
      const aTime = groupedLogs[a]?.[0]?.creationTime || 0;
      const bTime = groupedLogs[b]?.[0]?.creationTime || 0;
      return aTime - bTime;
    });
  }, [groupedLogs]);

  const totalCost = useMemo(() => {
    return logs.reduce(
      (sum, log) => sum + parseFloat(log?.totalCostUSD || 0),
      0,
    );
  }, [logs]);

  const totalInputTokens = useMemo(() => {
    return logs.reduce(
      (sum, log) => sum + parseInt(log?.inputTokens || 0, 10),
      0,
    );
  }, [logs]);

  const totalOutputTokens = useMemo(() => {
    return logs.reduce(
      (sum, log) => sum + parseInt(log?.outputTokens || 0, 10),
      0,
    );
  }, [logs]);

  const totalCachedTokens = useMemo(() => {
    return logs.reduce(
      (sum, log) => sum + parseInt(log?.cachedInputTokens || 0, 10),
      0,
    );
  }, [logs]);

  const totalCacheSavings = useMemo(() => {
    return logs.reduce(
      (sum, log) => sum + parseFloat(log?.cacheSavingsUSD || 0),
      0,
    );
  }, [logs]);

  const messageSnippet = (msgId) => {
    const msg = messages?.find((m) => m?._id === msgId);
    if (!msg) return null;
    const text = msg?.text || "";
    return text.length > 80 ? `${text.slice(0, 80)}...` : text;
  };

  const messageCost = (msgId) => {
    return (groupedLogs[msgId] || []).reduce(
      (sum, log) => sum + parseFloat(log?.totalCostUSD || 0),
      0,
    );
  };

  const toggleMessage = (msgId) => {
    setExpandedMessages((prev) => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  const _styles = StyleSheet.create({
    container: {
      borderRadius: 12,
      padding: 24,
      flex: 1,
    },
    headerSection: {
      marginBottom: 16,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: currentTheme?.theme?.borderColor || "rgba(0,0,0,0.1)",
    },
    chatIdRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginBottom: 8,
    },
    totalsRow: {
      flexDirection: "row",
      gap: 16,
      flexWrap: "wrap",
    },
    totalItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    content: {
      flex: 1,
    },
    messageGroup: {
      marginBottom: 12,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.borderColor || "rgba(0,0,0,0.08)",
      overflow: "hidden",
    },
    messageHeader: {
      flexDirection: "row",
      alignItems: "center",
      padding: 12,
      backgroundColor: currentTheme?.theme?.softBackgroundColor,
      gap: 10,
    },
    messageBody: {
      padding: 8,
      gap: 6,
    },
    logRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 10,
      paddingVertical: 8,
      paddingHorizontal: 10,
      backgroundColor: currentTheme?.theme?.backgroundColor,
      borderRadius: 8,
    },
    connector: {
      width: 2,
      alignSelf: "stretch",
      backgroundColor: currentTheme?.theme?.primary,
      opacity: 0.3,
      marginLeft: 21,
    },
    logDetails: {
      flex: 1,
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    badge: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      backgroundColor: currentTheme?.theme?.softBackgroundColor,
      borderRadius: 6,
      paddingHorizontal: 8,
      paddingVertical: 4,
    },
    costBadge: {
      backgroundColor: `${currentTheme?.theme?.primary}15`,
    },
    cacheBadge: {
      backgroundColor: `${currentTheme?.theme?.success || currentTheme?.theme?.primary}15`,
    },
    footer: {
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: "rgba(0,0,0,0.1)",
      alignItems: "center",
    },
    button: {
      paddingVertical: 12,
    },
  });

  return (
    <ToothFairyModal
      modalStyle={{
        height: "85%",
        width: "90%",
        maxWidth: isLargeScreen() ? 800 : 380,
        marginHorizontal: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        margin: "auto",
      }}
      isModalOpen={visible}
      setIsModalOpen={setVisible}
      modalTitle={""}
      closeModal={() => {
        setVisible(false);
      }}
    >
      <View
        style={[
          _styles.container,
          { backgroundColor: currentTheme?.theme?.backgroundColor },
        ]}
      >
        <View style={_styles.headerSection}>
          <View style={_styles.chatIdRow}>
            <AppIcon
              icon={AppAssets?.icons?.uoi}
              size={20}
              color={currentTheme?.theme?.primary}
            />
            <AppText
              style={{
                fontSize: 18,
                fontWeight: "bold",
                fontFamily: "normalFont",
                color: currentTheme?.theme?.primary,
              }}
            >
              {t("chats.ui.uoiUsage")}
            </AppText>
          </View>
          <AppText
            style={{
              fontSize: 12,
              fontFamily: "normalFont",
              color: currentTheme?.theme?.darkGrey,
            }}
          >
            {t("chats.ui.uoiUsageChatId")}: {truncateId(chatId, 20)}
          </AppText>
          <View style={[_styles.totalsRow, { marginTop: 12 }]}>
            <View style={_styles.totalItem}>
              <AppText
                style={{
                  fontSize: 12,
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.darkGrey,
                }}
              >
                {t("chats.ui.uoiUsageTotal")}:
              </AppText>
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: "bold",
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.primary,
                }}
              >
                {formatUoI(totalCost)}
              </AppText>
            </View>
            <View style={_styles.totalItem}>
              <AppText
                style={{
                  fontSize: 12,
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.darkGrey,
                }}
              >
                {t("chats.ui.uoiUsageInputTokens")}:
              </AppText>
              <AppText
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.font_color,
                }}
              >
                {totalInputTokens.toLocaleString()}
              </AppText>
            </View>
            <View style={_styles.totalItem}>
              <AppText
                style={{
                  fontSize: 12,
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.darkGrey,
                }}
              >
                {t("chats.ui.uoiUsageOutputTokens")}:
              </AppText>
              <AppText
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.font_color,
                }}
              >
                {totalOutputTokens.toLocaleString()}
              </AppText>
            </View>
            {totalCachedTokens > 0 && (
              <View style={_styles.totalItem}>
                <AppText
                  style={{
                    fontSize: 12,
                    fontFamily: "normalFont",
                    color: currentTheme?.theme?.darkGrey,
                  }}
                >
                  {t("chats.ui.uoiUsageCachedTokens")}:
                </AppText>
                <AppText
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    fontFamily: "normalFont",
                    color: currentTheme?.theme?.primary,
                  }}
                >
                  {totalCachedTokens.toLocaleString()}
                </AppText>
              </View>
            )}
            {totalCacheSavings > 0 && (
              <View style={_styles.totalItem}>
                <AppText
                  style={{
                    fontSize: 12,
                    fontFamily: "normalFont",
                    color: currentTheme?.theme?.darkGrey,
                  }}
                >
                  {t("chats.ui.uoiUsageCacheSavings")}:
                </AppText>
                <AppText
                  style={{
                    fontSize: 13,
                    fontWeight: "600",
                    fontFamily: "normalFont",
                    color: currentTheme?.theme?.success || currentTheme?.theme?.primary,
                  }}
                >
                  ${totalCacheSavings.toFixed(4)}
                </AppText>
              </View>
            )}
          </View>
        </View>

        <ScrollView
          style={_styles.content}
          showsVerticalScrollIndicator={false}
        >
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              caption="chats.ui.uoiUsageLoading"
              style={{ position: "relative", minHeight: 200 }}
            />
          ) : error ? (
            <View
              style={{
                padding: 40,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AppText
                style={{
                  fontSize: 16,
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.danger || "red",
                }}
              >
                {error}
              </AppText>
            </View>
          ) : messageOrder.length === 0 ? (
            <View
              style={{
                padding: 40,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <AppText
                style={{
                  fontSize: 16,
                  fontFamily: "normalFont",
                  color: currentTheme?.theme?.darkGrey,
                }}
              >
                {t("chats.ui.uoiUsageEmpty")}
              </AppText>
            </View>
          ) : (
            messageOrder.map((msgId, msgIndex) => {
              const msgLogs = groupedLogs[msgId] || [];
              const isExpanded = expandedMessages[msgId];
              const snippet = messageSnippet(msgId);
              return (
                <View key={msgId} style={_styles.messageGroup}>
                  <View
                    style={_styles.messageHeader}
                    accessibilityRole="button"
                  >
                    <AppIcon
                      icon={
                        isExpanded
                          ? AppAssets?.icons?.collapse
                          : AppAssets?.icons?.expand
                      }
                      size={14}
                      color={currentTheme?.theme?.darkGrey}
                      isClickable
                      onPress={() => toggleMessage(msgId)}
                    />
                    <View style={{ flex: 1 }}>
                      <AppText
                        style={{
                          fontSize: 13,
                          fontWeight: "bold",
                          fontFamily: "normalFont",
                          color: currentTheme?.theme?.font_color,
                        }}
                      >
                        {t("chats.ui.uoiUsageMessage")}: {truncateId(msgId, 16)}
                      </AppText>
                      {snippet && (
                        <AppText
                          style={{
                            fontSize: 11,
                            fontFamily: "normalFont",
                            color: currentTheme?.theme?.darkGrey,
                            marginTop: 2,
                          }}
                          numberOfLines={1}
                        >
                          {snippet}
                        </AppText>
                      )}
                    </View>
                    <View style={_styles.badge}>
                      <AppText
                        style={{
                          fontSize: 12,
                          fontWeight: "bold",
                          fontFamily: "normalFont",
                          color: currentTheme?.theme?.primary,
                        }}
                      >
                        {formatUoI(messageCost(msgId))}
                      </AppText>
                    </View>
                  </View>

                  {isExpanded && (
                    <View style={_styles.messageBody}>
                      {msgLogs.map((log, logIndex) => (
                        <View key={log?.id || logIndex}>
                          <View style={_styles.logRow}>
                            <AppIcon
                              icon={AppAssets?.icons?.uoi}
                              size={14}
                              color={currentTheme?.theme?.primary}
                            />
                            <View style={_styles.logDetails}>
                              <View style={_styles.badge}>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.darkGrey,
                                  }}
                                >
                                  {t("chats.ui.uoiUsageCallType")}:
                                </AppText>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontWeight: "600",
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.font_color,
                                  }}
                                >
                                  {log?.callType || "-"}
                                </AppText>
                              </View>
                              <View style={_styles.badge}>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.darkGrey,
                                  }}
                                >
                                  {t("chats.ui.uoiUsageCallPurpose")}:
                                </AppText>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontWeight: "600",
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.font_color,
                                  }}
                                >
                                  {log?.callPurpose || "-"}
                                </AppText>
                              </View>
                              <View style={[_styles.badge, _styles.costBadge]}>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontWeight: "bold",
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.white,
                                  }}
                                >
                                  UoI: {formatUoI(log?.totalCostUSD)}
                                </AppText>
                              </View>
                              <View style={_styles.badge}>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.darkGrey,
                                  }}
                                >
                                  {t("chats.ui.uoiUsageInputTokens")}:
                                </AppText>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontWeight: "600",
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.font_color,
                                  }}
                                >
                                  {parseInt(
                                    log?.inputTokens || 0,
                                    10,
                                  ).toLocaleString()}
                                </AppText>
                              </View>
                              <View style={_styles.badge}>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.darkGrey,
                                  }}
                                >
                                  {t("chats.ui.uoiUsageOutputTokens")}:
                                </AppText>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontWeight: "600",
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.font_color,
                                  }}
                                >
                                  {parseInt(
                                    log?.outputTokens || 0,
                                    10,
                                  ).toLocaleString()}
                                </AppText>
                              </View>
                              {parseInt(log?.cachedInputTokens || 0, 10) >
                                0 && (
                                <View
                                  style={[
                                    _styles.badge,
                                    _styles.cacheBadge,
                                  ]}
                                >
                                  <AppText
                                    style={{
                                      fontSize: 11,
                                      fontFamily: "normalFont",
                                      color: currentTheme?.theme?.darkGrey,
                                    }}
                                  >
                                    {t("chats.ui.uoiUsageCachedTokens")}:
                                  </AppText>
                                  <AppText
                                    style={{
                                      fontSize: 11,
                                      fontWeight: "600",
                                      fontFamily: "normalFont",
                                      color: currentTheme?.theme?.primary,
                                    }}
                                  >
                                    {parseInt(
                                      log?.cachedInputTokens || 0,
                                      10,
                                    ).toLocaleString()}
                                  </AppText>
                                </View>
                              )}
                              <View style={_styles.badge}>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.darkGrey,
                                  }}
                                >
                                  {t("chats.ui.uoiUsageModelType")}:
                                </AppText>
                                <AppText
                                  style={{
                                    fontSize: 11,
                                    fontWeight: "600",
                                    fontFamily: "normalFont",
                                    color: currentTheme?.theme?.font_color,
                                  }}
                                >
                                  {modelTypeLabels[log?.modelType] ||
                                    log?.modelType ||
                                    "-"}
                                </AppText>
                              </View>
                            </View>
                          </View>
                          {logIndex < msgLogs.length - 1 && (
                            <View style={_styles.connector} />
                          )}
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              );
            })
          )}
        </ScrollView>

        <View style={_styles.footer}>
          <ToothFairyButton
            title={t("common.close")}
            onPress={() => setVisible(false)}
            buttonStyle={_styles.button}
            primary
          />
        </View>
      </View>
    </ToothFairyModal>
  );
};

export default UoIUsageModal;
